/**
 * Country-code conventions, of which this system has two.
 *
 * Kindred's own data — coupons (`kindred-coupons.countryCodes`) and proxies
 * (`proxies.countryCode`) — uses **"UK"**, not the ISO "GB". That is not a
 * choice we get to make; it is what the data says, and 19,967 UK-tagged coupons
 * against zero GB-tagged ones is the whole argument.
 *
 * The fake-address table is the other way round: it is keyed on ISO, so it has
 * its own alias map pointing UK/EN/GBR at "GB" (see `fake-address-data.ts`).
 * Keep the two separate — they normalise towards different canonical forms for
 * different reasons.
 *
 * Why this matters concretely: the coupon lookup matches `countryCodes`
 * exactly, so a merchant stored as "GB" asked for GB-tagged coupons, found
 * none, and its run reported "no coupons found" while the merchant plainly had
 * live UK ones.
 */
const TO_KINDRED: Record<string, string> = {
  GB: "UK",
  GBR: "UK",
  EN: "UK",
  ENG: "UK",
  USA: "US",
  UAE: "AE",
  EL: "GR",
  GRC: "GR",
};

/**
 * Normalise a country code to the form Kindred's data is keyed on.
 * Case-insensitive; returns the input upper-cased when no alias applies.
 */
export const toKindredCountryCode = (
  countryCode: string | null | undefined
): string => {
  if (!countryCode) return "";
  const upper = String(countryCode).trim().toUpperCase();
  return TO_KINDRED[upper] ?? upper;
};

/** Every spelling that normalises to this Kindred code, itself included (UK → UK, GB, GBR, EN, ENG). */
export const kindredCountryAliases = (countryCode: string): string[] => {
  const code = toKindredCountryCode(countryCode);
  return [
    code,
    ...Object.keys(TO_KINDRED).filter((k) => TO_KINDRED[k] === code),
  ];
};
